import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useForm, Controller, useWatch, FormProvider } from "react-hook-form";
import dayjs from "dayjs";
import { Link, useSearchParams } from "react-router-dom";

import {
  Button,
  Card,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Grow,
  Collapse,
  Box,
  Stepper,
  Step,
  StepLabel,
  SelectChangeEvent,
  Breadcrumbs,
} from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { PageLayout } from "../../ui-components/PageLayout";

import FinancialDetails from "../../ui-components/Transaction/FinancialDetails";
import {
  DuplicateActionEnumModel,
  ICreatedEntityModel,
  ICreateTransactionModel,
  IFinancialDtoModel,
  ITransactionFormData,
} from "../../types/types";
import BasicTransaction from "../../ui-components/Transaction/BasicTransaction";
import { AppDispatch } from "../../store/store";
import { useDispatch, useSelector } from "react-redux";
import {
  getTransactionById,
  upsertTransactionDirect,
  upsertFinancials,
} from "../../store/modules/transactions/transactionActions";
import { RootState } from "../../store/reducers";
import LoadingDialog from "../../ui-components/Dialogs/LoadingDialog";
import { useParams } from "react-router-dom";
import dateOnlyToJson from "../../utils/dateOnlyToJson";
import { mapFinancialsToFormValues } from "../../utils/transactionFinancials";
import { getApiErrorMessage } from "../../utils/common";
import SuccessDialog from "../../ui-components/Dialogs/SuccessDialog";
import { mergeCreatedEntities } from "../../store/modules/common/commonStateActions";
import { getUserCustomData } from "../../store/modules/common/commonActions";
import DuplicateTransactionDialog from "../../ui-components/Dialogs/DuplicateTransactionDialog";
import {
  PIPELINE_ROUTE,
  TransactionStatus,
  isPipelineStatus,
} from "../../hooks/usePipeline";

dayjs.extend(dateOnlyToJson);
const TransactionEdit = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams] = useSearchParams();
  const [pendingCreatedEntities, setPendingCreatedEntities] =
    useState<ICreatedEntityModel | null>(null);

  const { transactionId } = useParams<{
    transactionId?: string;
  }>();
  const [selectedYearId, setSelectedYearId] = useState<number | undefined>();

  const { years } = useSelector((state: RootState) => state.transactionYear);
  const year = years.find((x) => x.id === selectedYearId)?.year ?? 0;

  const {
    loaded: userDataLoaded,
    loading: userDataLoading,
    data: userData,
  } = useSelector((state: RootState) => state.commonData.userData);

  const navigate = useNavigate();


  const methods = useForm<ITransactionFormData>({
    mode: "all",
  });

  const [activeStep, setActiveStep] = useState(0);
  const { control, handleSubmit, formState, getValues, trigger } = methods;

  const [loading, setLoading] = useState(false);

  const [loadError, setLoadError] = useState<string | null>(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  // A Pipeline entry switched to Transaction goes through the duplicate check like a new one
  const [duplicatePrompt, setDuplicatePrompt] = useState<{
    duplicateId: number;
    resolve: (action: DuplicateActionEnumModel | null) => void;
  } | null>(null);

  const saveTransaction = async (transactionData: ICreateTransactionModel) => {
    const result = await upsertTransactionDirect(transactionData);
    if (!result.duplicateId) return result;
    const action = await new Promise<DuplicateActionEnumModel | null>(
      (resolve) =>
        setDuplicatePrompt({ duplicateId: result.duplicateId!, resolve }),
    );
    setDuplicatePrompt(null);
    if (action === null || Number(action) === DuplicateActionEnumModel.Skip) {
      return null;
    }
    return upsertTransactionDirect({
      ...transactionData,
      forceDuplicate: true,
      replace: Number(action) === DuplicateActionEnumModel.Overwrite,
    });
  };
  useEffect(() => {
    if (pendingCreatedEntities) {
      dispatch(mergeCreatedEntities(pendingCreatedEntities));
      setPendingCreatedEntities(null);
    }
  }, [pendingCreatedEntities, dispatch]);

  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam === "financials") {
      setActiveStep(1);
    } else {
      setActiveStep(0);
    }
  }, [searchParams]);

  const {
    data,
    isSuccess,
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: ["transaction", transactionId],
    queryFn: async () => {
      if (!transactionId) return;
      try {
        const result = await getTransactionById(Number(transactionId));
        return result;
      } catch (err) {
        throw new Error("Failed to load transaction details");
      }
    },
    refetchOnWindowFocus: false,
    enabled: !!transactionId,
  });
  // Saved status decides which folder the breadcrumb goes back to
  const isPipelineEntry = isPipelineStatus(data?.transactionDetail?.status);

  // Add a form change listener to ensure we're correctly tracking form changes
  useEffect(() => {
    const subscription = methods.watch((value, { name, type }) => {
      if (type === "change") {
        // console.log(
        //   `Field ${name} changed to:`,
        //   value[name as keyof typeof value]
        // );
        // // Access formState to ensure React Hook Form updates the dirty state
        const isDirty = methods.formState.isDirty;
        // console.log("Form is now dirty:", isDirty);
      }
    });

    return () => subscription.unsubscribe();
  }, [methods]);

  useEffect(() => {
    if (isSuccess && data) {
      const transaction = data.transactionDetail;
      if (!transaction) {
        setLoadError("Transaction not found");
        return;
      }

      setSelectedYearId(transaction.yearId);

      // Store the initial form values
      const formValues = {
        id: Number(transactionId),
        yearId: transaction?.yearId,
        source: transaction?.sourceId ?? null,
        // additionalCharges:
        //   transaction?.financials?.brokerageAdditionalChargePercentage ?? null,
        address: transaction?.address ?? null,
        area: transaction?.areaId
          ? (userData?.areas?.find((x) => x.id === transaction?.areaId) ?? null)
          : null,

        price: transaction?.price ?? null,

        status: transaction?.status ?? TransactionStatus.Transaction,

        leadReceivedDate: transaction?.leadReceivedDate ?? null,
        date: transaction?.date!,
        // flatCommission: transaction?.financials?.flatCommissionAmount ?? null,
        reason: transaction?.reasonId ?? null,

        transactionType: transaction?.typeId ?? null,
        clients: transaction?.clients,
        soiReferrers: transaction?.soiReferrers,
        agentReferrers: transaction?.agentReferrers,
        vendorReferrers: transaction?.vendorReferrers ?? [],
        vendors: transaction?.vendors ?? [],
        agentReferrered: transaction?.agentsReferred ?? [],
        referralOutTypeId: transaction?.referralOutTypeId ?? null,
        notes: transaction?.notes ?? null,
        specialGroups: transaction?.specialGroups
          ? transaction?.specialGroups?.map(
              (id) => userData?.specialGroups?.find((x) => x.id === id)!,
            )
          : null,
        specialTags: transaction?.specialTags
          ? transaction?.specialTags?.map(
              (id) => userData?.specialTags?.find((x) => x.id === id)!,
            )
          : null,
        coAgentMember: transaction?.coAgentId
          ? userData?.coAgents?.find((x) => x.id === transaction!.coAgentId)
          : null,
        coAgentMember2: transaction?.coAgent2Id
          ? userData?.coAgents?.find((x) => x.id === transaction!.coAgent2Id)
          : null,
        ...mapFinancialsToFormValues(transaction?.financials),
      };

      // Reset the form with the initial values
      setTimeout(() => {
        methods.reset(formValues);
      }, 0);
    } else if (queryError) {
      setLoadError(
        queryError instanceof Error
          ? queryError.message
          : "Failed to load transaction",
      );
    }
  }, [isSuccess, data, queryError, methods, transactionId]);

  // Server-side rejections (e.g. the Lead Received Date rule) go in the errors box
  const showSaveError = (err: unknown) =>
    methods.setError("root", {
      type: "server",
      message: getApiErrorMessage(
        err,
        "Failed to save transaction. Please try again.",
      ),
    });

  const handleSave = async () => {
    methods.clearErrors("root");
    const isValid = await trigger([
      "transactionType",
      "price",
      "date",
      "leadReceivedDate",
      "address",
    ]);
    if (isValid) {
      try {
        setLoading(true);
        const data = getValues();
        const transactionData = {
          transactionId: Number(transactionId),
          status: data.status ?? TransactionStatus.Transaction,
          leadReceivedDate: data.leadReceivedDate ?? undefined,
          date: data.date ?? undefined,
          price: data.price,
          typeId: data.transactionType,
          notes: data.notes,
          address: data.address,
          sourceId: data.source,
          reasonId: data.reason,
          referraloUtTypeId: data.referralOutTypeId,
          areaId: (data.area?.id ?? 0) > 0 ? data.area?.id : undefined,
          insertedArea: (data.area?.id ?? 0) > 0 ? undefined : data.area?.name,
          coAgentId:
            (data.coAgentMember?.id ?? 0) > 0
              ? data.coAgentMember?.id
              : undefined,
          insertedCoAgent:
            (data.coAgentMember?.id ?? 0) <= 0
              ? data.coAgentMember?.name
              : undefined,
          coAgent2Id:
            (data.coAgentMember2?.id ?? 0) > 0
              ? data.coAgentMember2?.id
              : undefined,
          insertedCoAgent2:
            (data.coAgentMember2?.id ?? 0) <= 0
              ? data.coAgentMember2?.name
              : undefined,
          selectedClients: data.clients
            ?.filter((f) => f.id! > 0)
            .map((client) => client.id),
          insertedClients: data.clients
            ?.filter((f) => f.id! <= 0)
            .map((client) => client.name),
          selectedGroups: data.specialGroups
            ?.filter((f) => f.id! > 0)
            .map((group) => group.id),
          insertedGroups: [
            ...new Set(
              data.specialGroups
                ?.filter((f) => f.id! <= 0)
                .map((group) => group.name),
            ),
          ],
          selectedTags: data.specialTags
            ?.filter((f) => f.id! > 0)
            .map((tag) => tag.id),
          insertedTags: [
            ...new Set(
              data.specialTags
                ?.filter((f) => f.id! <= 0)
                .map((tag) => tag.name),
            ),
          ],
          selectedSoiReferrers: data.soiReferrers
            ?.filter((f) => f.id! > 0)
            .map((referrer) => referrer.id),
          insertedSoiReferrers: data.soiReferrers
            ?.filter((f) => f.id! <= 0)
            .map((referrer) => referrer.name),
          selectedAgentReferrers: data.agentReferrers
            ?.filter((f) => f.id! > 0)
            .map((referrer) => referrer.id),
          insertedAgentReferrers: data.agentReferrers
            ?.filter((f) => f.id! <= 0)
            .map((referrer) => referrer.name),
          selectedVendorReferrers: data.vendorReferrers
            ?.filter((f) => f.id! > 0)
            .map((referrer) => referrer.id),
          insertedVendorReferrers: data.vendorReferrers
            ?.filter((f) => f.id! <= 0)
            .map((referrer) => referrer.name),
          selectedVendors: data.vendors
            ?.filter((f) => f.id! > 0)
            .map((vendor) => vendor.id),
          insertedVendors: data.vendors
            ?.filter((f) => f.id! <= 0)
            .map((vendor) => vendor.name),
          selectedAgentReferredOut: data.agentReferrered
            ?.filter((f) => f.id! > 0)
            .map((referrer) => referrer.id),
          insertedAgentReferredOut: data.agentReferrered
            ?.filter((f) => f.id! <= 0)
            .map((referrer) => referrer.name),
          referralOutTypeId: data.referralOutTypeId,
        } as ICreateTransactionModel;

        var result = await saveTransaction(transactionData);
        if (!result) return;
        if (result.createdEntities) {
          setPendingCreatedEntities(
            result.createdEntities.toJSON() as ICreatedEntityModel,
          );
        }
        navigate(`/transactions/view/${transactionData.transactionId}`);
        // setShowSuccessDialog(true);
      } catch (err) {
        console.error("Transaction update failed:", err);
        dispatch(getUserCustomData());
        showSaveError(err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleGoToFinances = () => {
    setActiveStep(1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleNext = async () => {
    methods.clearErrors("root");
    const isValid = await trigger([
      "transactionType",
      "price",
      "date",
      "leadReceivedDate",
      "address",
    ]);
    if (isValid) {
      // Always save when going to finances, regardless of dirty state
      try {
        setLoading(true);
        const data = getValues();
        // console.log("Form dirty state:", formState.isDirty);
        // console.log("Form values:", data);

        const transactionData = {
          transactionId: Number(transactionId),
          status: data.status ?? TransactionStatus.Transaction,
          leadReceivedDate: data.leadReceivedDate ?? undefined,
          date: data.date ?? undefined,
          price: data.price,
          typeId: data.transactionType,
          notes: data.notes,
          address: data.address,
          sourceId: data.source,
          reasonId: data.reason,
          referralOutTypeId: data.referralOutTypeId,
          areaId: (data.area?.id ?? 0) > 0 ? data.area?.id : undefined,
          insertedArea: (data.area?.id ?? 0) > 0 ? undefined : data.area?.name,
          coAgentId:
            (data.coAgentMember?.id ?? 0) > 0
              ? data.coAgentMember?.id
              : undefined,
          insertedCoAgent:
            (data.coAgentMember?.id ?? 0) <= 0
              ? data.coAgentMember?.name
              : undefined,
          coAgent2Id:
            (data.coAgentMember2?.id ?? 0) > 0
              ? data.coAgentMember2?.id
              : undefined,
          insertedCoAgent2:
            (data.coAgentMember2?.id ?? 0) <= 0
              ? data.coAgentMember2?.name
              : undefined,
          selectedClients: data.clients
            ?.filter((f) => f.id! > 0)
            .map((client) => client.id),
          insertedClients: data.clients
            ?.filter((f) => f.id! <= 0)
            .map((client) => client.name),
          selectedGroups: data.specialGroups
            ?.filter((f) => f.id! > 0)
            .map((group) => group.id),
          insertedGroups: data.specialGroups
            ?.filter((f) => f.id! <= 0)
            .map((group) => group.name),
          selectedTags: data.specialTags
            ?.filter((f) => f.id! > 0)
            .map((tag) => tag.id),
          insertedTags: data.specialTags
            ?.filter((f) => f.id! <= 0)
            .map((tag) => tag.name),
          selectedSoiReferrers: data.soiReferrers
            ?.filter((f) => f.id! > 0)
            .map((referrer) => referrer.id),
          insertedSoiReferrers: data.soiReferrers
            ?.filter((f) => f.id! <= 0)
            .map((referrer) => referrer.name),
          selectedAgentReferrers: data.agentReferrers
            ?.filter((f) => f.id! > 0)
            .map((referrer) => referrer.id),
          insertedAgentReferrers: data.agentReferrers
            ?.filter((f) => f.id! <= 0)
            .map((referrer) => referrer.name),
          selectedVendorReferrers: data.vendorReferrers
            ?.filter((f) => f.id! > 0)
            .map((referrer) => referrer.id),
          insertedVendorReferrers: data.vendorReferrers
            ?.filter((f) => f.id! <= 0)
            .map((referrer) => referrer.name),
          selectedVendors: data.vendors
            ?.filter((f) => f.id! > 0)
            .map((vendor) => vendor.id),
          insertedVendors: data.vendors
            ?.filter((f) => f.id! <= 0)
            .map((vendor) => vendor.name),
        } as ICreateTransactionModel;

        var result = await saveTransaction(transactionData);
        if (!result) return;
        if (result.createdEntities) {
          dispatch(
            mergeCreatedEntities(
              result.createdEntities.toJSON() as ICreatedEntityModel,
            ),
          );
        }

        // Now proceed to step 2
        setActiveStep((prev) => prev + 1);
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch (err) {
        console.error("Transaction update failed:", err);
        dispatch(getUserCustomData());

        showSaveError(err);
      } finally {
        setLoading(false);
      }
    }
  };
  const handleYearChange = (event: SelectChangeEvent) => {
    if (event.target.value) setSelectedYearId(Number(event.target.value));
    else {
      setSelectedYearId(undefined);
    }
  };
  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onSubmit = async (data: ITransactionFormData) => {
    methods.clearErrors("root");
    try {
      setLoading(true);

      if (activeStep === 0) {
        const isValid = await trigger([
          "transactionType",
          "price",
          "date",
          "leadReceivedDate",
          "address",
        ]);

        if (!isValid) {
          setLoading(false);
          return;
        }
        const transactionData = {
          transactionId: Number(transactionId),
          status: data.status ?? TransactionStatus.Transaction,
          leadReceivedDate: data.leadReceivedDate ?? undefined,
          date: data.date ?? undefined,
          price: data.price,
          typeId: data.transactionType,
          notes: data.notes,
          address: data.address,
          sourceId: data.source,
          reasonId: data.reason,
          referralOutTypeId: data.referralOutTypeId,
          areaId: (data.area?.id ?? 0) > 0 ? data.area?.id : undefined,
          insertedArea: (data.area?.id ?? 0) > 0 ? undefined : data.area?.name,
          coAgentId:
            (data.coAgentMember?.id ?? 0) > 0
              ? data.coAgentMember?.id
              : undefined,
          insertedCoAgent:
            (data.coAgentMember?.id ?? 0) <= 0
              ? data.coAgentMember?.name
              : undefined,
          coAgent2Id:
            (data.coAgentMember2?.id ?? 0) > 0
              ? data.coAgentMember2?.id
              : undefined,
          insertedCoAgent2:
            (data.coAgentMember2?.id ?? 0) <= 0
              ? data.coAgentMember2?.name
              : undefined,
          selectedClients: data.clients
            ?.filter((f) => f.id! > 0)
            .map((client) => client.id),
          insertedClients: data.clients
            ?.filter((f) => f.id! <= 0)
            .map((client) => client.name),
          selectedGroups: data.specialGroups
            ?.filter((f) => f.id! > 0)
            .map((group) => group.id),
          insertedGroups: data.specialGroups
            ?.filter((f) => f.id! <= 0)
            .map((group) => group.name),
          selectedTags: data.specialTags
            ?.filter((f) => f.id! > 0)
            .map((tag) => tag.id),
          insertedTags: data.specialTags
            ?.filter((f) => f.id! <= 0)
            .map((tag) => tag.name),
          selectedSoiReferrers: data.soiReferrers
            ?.filter((f) => f.id! > 0)
            .map((referrer) => referrer.id),
          insertedSoiReferrers: data.soiReferrers
            ?.filter((f) => f.id! <= 0)
            .map((referrer) => referrer.name),
          selectedAgentReferrers: data.agentReferrers
            ?.filter((f) => f.id! > 0)
            .map((referrer) => referrer.id),
          insertedAgentReferrers: data.agentReferrers
            ?.filter((f) => f.id! <= 0)
            .map((referrer) => referrer.name),
          selectedVendorReferrers: data.vendorReferrers
            ?.filter((f) => f.id! > 0)
            .map((referrer) => referrer.id),
          insertedVendorReferrers: data.vendorReferrers
            ?.filter((f) => f.id! <= 0)
            .map((referrer) => referrer.name),
          selectedVendors: data.vendors
            ?.filter((f) => f.id! > 0)
            .map((vendor) => vendor.id),
          insertedVendors: data.vendors
            ?.filter((f) => f.id! <= 0)
            .map((vendor) => vendor.name),
        } as ICreateTransactionModel;

        var result = await saveTransaction(transactionData);
        if (!result) return;
        if (result.createdEntities) {
          setPendingCreatedEntities(
            result.createdEntities.toJSON() as ICreatedEntityModel,
          );
        }
        navigate(`/transactions/view/${transactionData.transactionId}`);
        // setShowSuccessDialog(true);
      } else {
        // If we're in step 2, update the financials
        if (transactionId) {
          const financialData = {
            commissionPercentage:
              data.commissionPercentage?.toString().trim() === ""
                ? null
                : data.commissionPercentage,
            flatCommissionAmount:
              data.flatCommission?.toString().trim() === ""
                ? null
                : data.flatCommission,
            clientRebatePercentage:
              data.clientRebatePrice?.toString().trim() === ""
                ? null
                : data.clientRebatePrice,
            clientRebateGciPercentage:
              data.clientRebateGCI?.toString().trim() === ""
                ? null
                : data.clientRebateGCI,
            clientRebateFlatAmount:
              data.clientRebateFlatAmount?.toString().trim() === ""
                ? null
                : data.clientRebateFlatAmount,
            referralFeePaidPercentage:
              data.referralFeePaid?.toString().trim() === ""
                ? null
                : data.referralFeePaid,
            referralFeeReceivedPercentage:
              data.referralFeeReceived?.toString().trim() === ""
                ? null
                : data.referralFeeReceived,
            coAgentSplitPaidPercentage:
              data.coAgentSplit?.toString().trim() === ""
                ? null
                : data.coAgentSplit,
            coAgentSplit2PaidPercentage:
              data.coAgentSplit2?.toString().trim() === ""
                ? null
                : data.coAgentSplit2,
            brokerageSplitPercentage:
              data.brokerageSplit?.toString().trim() === ""
                ? null
                : data.brokerageSplit,
            brokerageAdditionalChargePercentage:
              data.additionalCharges?.toString().trim() === ""
                ? null
                : data.additionalCharges,
            brokerageFlatFees:
              data.brkFlatFees?.toString().trim() === ""
                ? null
                : data.brkFlatFees,
            incentivesAmount:
              data.incentivesPaid?.toString().trim() === ""
                ? null
                : data.incentivesPaid,
            brokerageAnniversaryDate:
              data.brkAnniversary?.toString().trim() === ""
                ? null
                : data.brkAnniversary,
            date: data.date,
          } as IFinancialDtoModel;

          await upsertFinancials(Number(transactionId), financialData);
          navigate(
            `/transactions/view/${Number(transactionId)}?tab=financials`,
          );
        }
      }
    } catch (error) {
      console.error("Error submitting transaction:", error);
      dispatch(getUserCustomData());

      showSaveError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayout>
      <LoadingDialog open={loading || userDataLoading || isLoading} />
      <DuplicateTransactionDialog
        open={duplicatePrompt !== null}
        onClose={() => duplicatePrompt?.resolve(null)}
        onViewDuplicate={() =>
          window.open(
            `/transactions/view/${duplicatePrompt?.duplicateId}`,
            "_blank",
          )
        }
        onProceed={(action) => duplicatePrompt?.resolve(action)}
        duplicateId={duplicatePrompt?.duplicateId ?? null}
      />
      {transactionId && (
        <SuccessDialog
          open={showSuccessDialog}
          onClose={() => setShowSuccessDialog(false)}
          onViewClick={() => {
            setShowSuccessDialog(false);
            navigate(`/transactions/view/${transactionId}`);
          }}
          header="Transaction Updated Successfully"
          message="Your transaction was successfully updated!"
          showFinancesOption={activeStep === 0}
          onGoToFinances={handleGoToFinances}
        />
      )}
      <Box sx={{ mb: 6, ml: 1 }}>
        <Box sx={{ mb: 2 }}>
          <Breadcrumbs aria-label="breadcrumb">
            <Link color="inherit" to="/dashboard">
              Dashboard
            </Link>
            <Link
              // component={RouterLink}
              to={`/transactions/${isPipelineEntry ? PIPELINE_ROUTE : year}`}
              color="inherit"
              // sx={{ "&:hover": { textDecoration: "underline" } }}
            >
              {isPipelineEntry ? "Pipeline" : `Transactions for ${year}`}
            </Link>
            <Typography color="text.primary">Edit</Typography>
          </Breadcrumbs>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography
            variant="h4"
            color="primary"
            component="h1"
            fontWeight="bold"
          >
            Edit Transaction
          </Typography>
          {/* <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Year:
            </Typography>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={selectedYearId?.toString() ?? ""}
                onChange={handleYearChange}
                variant="outlined"
              >
                {years.map((year) => (
                  <MenuItem key={year.id} value={year.id}>
                    {year.year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box> */}
        </Box>
        <FormProvider {...methods}>
          <Grid container spacing={3} padding={3}>
            <Grid item xs={12} pb={2}>
              <Stepper activeStep={activeStep} sx={{ mt: 3 }}>
                <Step>
                  <StepLabel>Transaction Details</StepLabel>
                </Step>
                <Step>
                  <StepLabel>Financial Details</StepLabel>
                </Step>
              </Stepper>
            </Grid>
            <Grid item xs={12} pb={2} sx={{ textAlign: "right", mt: 3 }}>
              {activeStep === 1 && (
                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleBack}
                  sx={{
                    mr: 2,
                    textTransform: "none",
                    px: 4,
                    py: 1.5,
                    minWidth: "160px",
                  }}
                >
                  Go to Main Info
                </Button>
              )}
              {activeStep === 0 && (
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleSave}
                  sx={{
                    backgroundColor: "primary.main",
                    "&:hover": { backgroundColor: "primary.dark" },
                    textTransform: "none",
                    px: 4,
                    py: 1.5,
                    minWidth: "160px",
                    mr: 2,
                  }}
                >
                  Save
                </Button>
              )}
              <Button
                variant={activeStep === 0 ? "outlined" : "contained"}
                size="large"
                onClick={
                  activeStep === 0 ? handleNext : handleSubmit(onSubmit)
                }
                sx={{
                  backgroundColor: activeStep === 0 ? "" : "primary.main",
                  "&:hover": {
                    backgroundColor: activeStep === 0 ? "" : "primary.dark",
                  },
                  textTransform: "none",
                  px: 4,
                  py: 1.5,
                  minWidth: "160px",
                }}
              >
                {activeStep === 0 ? "Go to Financials" : "Save"}
              </Button>
            </Grid>
            <>
              <Collapse in={activeStep === 0} sx={{ width: "100%" }}>
                <Card sx={{ p: 3, mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Transaction Details
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <BasicTransaction
                        year={
                          years.find((x) => x.id === selectedYearId)?.year ??
                          0
                        }
                      />
                    </Grid>
                    {/* <Grid item xs={12} sx={{ textAlign: "right" }}>
                      <Button
                        variant="contained"
                        size="large"
                        sx={{
                          backgroundColor: "primary.main",
                          "&:hover": { backgroundColor: "primary.dark" },
                          textTransform: "none",
                          px: 4,
                          py: 1.5,
                          minWidth: "160px",
                        }}
                        onClick={handleNext}
                      >
                        Next
                      </Button>
                    </Grid> */}
                  </Grid>
                </Card>
              </Collapse>

              <Collapse in={activeStep === 1} sx={{ width: "100%" }}>
                <Card sx={{ p: 3, mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Financial Details
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <FinancialDetails
                        isEdit={true}
                        // yearId={selectedYearId}
                        isFinancialTab={activeStep === 1}
                      />
                    </Grid>
                  </Grid>
                </Card>
              </Collapse>
            </>
          </Grid>
          <Grid item xs={12}>
            {Object.keys(formState.errors).length > 0 && (
              <Box
                sx={{
                  width: "100%",
                  mb: 2,
                  p: 2,
                  backgroundColor: "#ffeeee",
                  border: "1px solid #ffcccc",
                  borderRadius: 1,
                }}
              >
                <Typography variant="subtitle1" color="error" gutterBottom>
                  Please fix these errors:
                </Typography>
                {Object.entries(formState.errors).map(
                  ([fieldName, error], index) => (
                    <Typography key={index} variant="body2" color="error">
                      • {error?.message || `Invalid value in ${fieldName}`}
                    </Typography>
                  ),
                )}
              </Box>
            )}
          </Grid>
          <Grid item xs={12} sx={{ textAlign: "right", mt: 3 }}>
            {activeStep === 1 && (
              <Button
                variant="outlined"
                size="large"
                onClick={handleBack}
                sx={{
                  mr: 2,
                  textTransform: "none",
                  px: 4,
                  py: 1.5,
                  minWidth: "160px",
                }}
              >
                Go to Main Info
              </Button>
            )}
            {activeStep === 0 && (
              <Button
                variant="contained"
                size="large"
                onClick={handleSave}
                sx={{
                  backgroundColor: "primary.main",
                  "&:hover": { backgroundColor: "primary.dark" },
                  textTransform: "none",
                  px: 4,
                  py: 1.5,
                  minWidth: "160px",
                  mr: 2,
                }}
              >
                Save
              </Button>
            )}
            <Button
              variant={activeStep === 0 ? "outlined" : "contained"}
              // variant="outlined"
              size="large"
              onClick={activeStep === 0 ? handleNext : handleSubmit(onSubmit)}
              sx={{
                backgroundColor: activeStep === 0 ? "" : "primary.main",
                "&:hover": {
                  backgroundColor: activeStep === 0 ? "" : "primary.dark",
                },
                textTransform: "none",
                px: 4,
                py: 1.5,
                minWidth: "160px",
              }}
            >
              {activeStep === 0 ? "Go to Financials" : "Save"}
            </Button>
          </Grid>
        </FormProvider>
      </Box>
    </PageLayout>
  );
};

export default TransactionEdit;
